import React, {
	useCallback,
	useMemo,
	useState
} from 'react';
import {
	Button,
	Divider,
	Form,
	Icon,
	Label,
	Message,
	Segment
} from 'semantic-ui-react';

import { stateOptions } from '../../../services/constants';
import {
	validateEmail,
	validatePhone,
	validateZip
} from '../../../services/validation';

import useForm from '../../../hooks/useForm';

import { updateAgency } from '../../../api/nora/agencies';

import { useAgencyState } from '../../../contexts/AgenciesContext';
import { useAuthState } from '../../../contexts/AuthContext';
import { useUIAction } from '../../../contexts/UIContext';

import EmailValidationInfoPopup from '../../Form/EmailValidationInfoPopup';
import FormCheckbox from '../../Form/FormCheckbox';
import FormInput from '../../Form/FormInput';
import FormSelect from '../../Form/FormSelect';
import HelpModal from '../../Shared/HelpModal';
import PermissionsWrapper from '../../PermissionsWrapper';
import PhoneValidationInfoPopup from '../../Form/PhoneValidationInfoPopup';

import styles from '../agency-portal.module.css';

const Agency = () => {
	const {
		agency,
		agencyTypes
	} = useAgencyState();
	const { user: { permissions } } = useAuthState();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ forceValidation, setForceValidation ] = useState( false );
	const [ editable, setEditable ] = useState( false );

	const {
		formData,
		updateField,
		reinitializeFormData,
		resetFormData
	} = useForm( {
		...agency,
		type: agency.type.id,
		phone: agency.phone.number,
		ext: agency.phone.extension,
		SMSEnabled: agency.phone.isSMSEnabled,
		fax: agency.fax.number
	} );

	const errors = useMemo( () => ( {
		zip: [
			{
				error: Boolean( formData.zip ) && !validateZip( formData.zip ),
				message: 'Please enter a valid, 5-digit zip code.',
				fieldLabel: 'Zip'
			}
		],
		email: [
			{
				error: !formData.email,
				message: 'Email is a required field.',
				fieldLabel: 'Email'
			},
			{
				error: Boolean( formData.email ) && !validateEmail( formData.email ),
				message: <>Please enter a valid email address.&nbsp;<EmailValidationInfoPopup /></>,
				fieldLabel: 'Email'
			}
		],
		phone: [
			{
				error: !formData.phone,
				message: 'Phone is a required field.',
				fieldLabel: 'Phone'
			},
			{
				error: Boolean( formData.phone ) && !validatePhone( formData.phone ),
				message: <>Please enter a valid U.S. phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Phone'
			}
		],
		fax: [
			{
				error: Boolean( formData.fax ) && !validatePhone( formData.fax ),
				message: <>Please enter a valid U.S. Phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Fax'
			}
		]
	} ), [
		formData.email,
		formData.fax,
		formData.phone,
		formData.zip
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const handleEdit = useCallback( () => setEditable( true ), [] );

	const handleSave = useCallback( async () => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setInfobarLoader( 'Saving Agency Info...' );
			setForceValidation( false );

			let {
				type,
				phone,
				ext,
				SMSEnabled,
				fax,
				...rest
			} = formData;
			const data = {
				...rest,
				type: { id: type },
				phone: {
					number: phone,
					extension: ext,
					isSMSEnabled: SMSEnabled
				},
				fax: { number: fax }
			};

			try {
				await updateAgency( data );

				reinitializeFormData();
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			setInfobarLoader( '' );
			setEditable( false );
		}
	}, [
		forceValidation,
		formData,
		reinitializeFormData,
		setApiErrorAlert,
		setInfobarLoader,
		validate
	] );

	const handleCancel = useCallback( () => {
		setForceValidation( false );
		resetFormData();
		setEditable( false );
	}, [ resetFormData ] );

	const emailIcon = useMemo(
		() => !errors.email.some( error => error.error )
			? <Icon
				color="black"
				link
				name="mail"
				onClick={ () => console.log( 'email agency' ) }
			/>
			: null
		, [ errors.email ]
	);

	const websiteIcon = useMemo(
		() => formData.website
			? <Icon
				color="black"
				link
				name="globe"
				onClick={ () => console.log( 'visit agency website' ) }
			/>
			: null
		, [ formData.website ]
	);

	const countryCode = useMemo( () => <Label basic><span>+1&nbsp;</span></Label>, [] );

	return (
		<>
			<div className={ styles.container } >
				<header className={ styles.pageHeader }>
					<h2>Agency Details
						<HelpModal
							id={ 1 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</h2>
					<PermissionsWrapper permission={ permissions.agencyEditAgency.isAvailable }>
						{ editable
							? <div>
								<Button
									icon="save"
									inverted
									onClick={ handleSave }
								/>
								<Button
									icon="times"
									inverted
									onClick={ handleCancel }
								/>
							</div>
							: <Button
								disabled={ !permissions.agencyEditAgency.canExecute }
								icon="pencil"
								inverted
								onClick={ handleEdit }
								size="tiny"
							/>
						}
					</PermissionsWrapper>
				</header>
				<Segment
					basic
					as="div"
					className={ styles.content }
				>
					<div className={ styles.formContainer }>
						<Form
							autoComplete="off"
							error={ forceValidation && validate() }
						>
							<Form.Group>
								<FormInput
									fieldLabel="Agency Name"
									name="name"
									readOnly
									required
									value={ formData.name }
									width={ 8 }
								/>
								<FormSelect
									fieldLabel="Agency Type"
									name="agencyType"
									options={ agencyTypes }
									readOnly
									required
									value={ formData.type }
									width={ 6 }
								/>
							</Form.Group>

							<Divider
								hidden
								style={ { margin: '1.5rem 0' }}
							/>

							<Form.Group>
								<FormInput
									fieldLabel="Address"
									name="address"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.address }
									width={ 16 }
								/>
							</Form.Group>

							<Form.Group>
								<FormInput
									fieldLabel="Address Line 2"
									name="address2"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.address2 }
									width={ 16 }
								/>
							</Form.Group>

							<Form.Group>
								<FormInput
									fieldLabel="City"
									name="city"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.city }
									width={ 8 }
								/>
								<FormSelect
									fieldLabel="State"
									name="state"
									onChange={ updateField }
									options={ stateOptions }
									readOnly={ !editable }
									value={ formData.state }
									width={ 3 }
								/>
								<FormInput
									errors={ errors.zip }
									fieldLabel="Zip"
									forceValidation={ forceValidation }
									name="zip"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.zip }
									width={ 4 }
								/>
							</Form.Group>

							<Divider
								hidden
								style={ { margin: '1.5rem 0' }}
							/>

							<Form.Group>
								<FormInput
									errors={ errors.email }
									fieldLabel="Email"
									forceValidation={ forceValidation }
									icon={ emailIcon }
									maxLength={ 254 }
									name="email"
									onChange={ updateField }
									readOnly={ !editable }
									required
									value={ formData.email || '' }
									width={ 8 }
								/>
								<FormInput
									fieldLabel="Website"
									icon={ websiteIcon }
									maxLength={ 254 }
									name="website"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.website || '' }
									width={ 8 }
								/>
							</Form.Group>

							<Form.Group>
								<FormInput
									errors={ errors.phone }
									fieldLabel="Phone"
									forceValidation={ forceValidation }
									keyDown="integer"
									inputLabel={ countryCode }
									mask="phone"
									maxLength={ 14 }
									name="phone"
									onChange={ updateField }
									readOnly={ !editable }
									required
									value={ formData.phone || '' }
									width={ 5 }
								/>
								<FormInput
									fieldLabel="Ext."
									forceValidation={ forceValidation }
									keyDown="integer"
									name="ext"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.ext || '' }
									width={ 3 }
								/>
								<FormCheckbox
									checked={ formData.SMSEnabled }
									fieldLabel="SMS Enabled"
									name="SMSEnabled"
									onChange={ updateField }
									readOnly={ !editable }
									width={ 3 }
								/>
							</Form.Group>
							<Form.Group>
								<FormInput
									errors={ errors.fax }
									fieldLabel="Fax"
									forceValidation={ forceValidation }
									keyDown="integer"
									inputLabel={ countryCode }
									mask="phone"
									maxLength={ 14 }
									name="fax"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.fax || '' }
									width={ 8 }
								/>
							</Form.Group>
							<Message
								error
								size="small"
							>
								<Message.Header>
									There are errors in your submission. Please correct the following fields.
								</Message.Header>
								<br />
								<Message.List>
									{ mapFormErrors() }
								</Message.List>
							</Message>
						</Form>
					</div>
				</Segment>
			</div>
			<Divider hidden />
		</>
	);
};

export default Agency;
